/**
 * DiffText — text diff renderer (Phase 23.2.2 — Living SOPs)
 *
 * Supports two granularities:
 *  - 'paragraph' — show before as <del>, after as <ins> (or plain if equal)
 *  - 'word'      — use diff-match-patch to produce character-level diff
 *
 * `mode='base'` renders the `before` text as plain text (no diff decoration).
 * Used on the left column of the redline for the BEFORE view.
 *
 * `mode='diff'` renders the AFTER view with either strike-through (deleted
 * text) or green-highlighted (inserted text) segments.
 *
 * The word-level diff uses diff-match-patch's `diff_main` + `diff_cleanupSemantic`
 * to produce human-readable diff segments. Character-level without
 * `cleanupSemantic` would produce too much visual noise on typical step-text
 * edits (e.g., shifting a single word creates dozens of one-char diffs).
 */

import { diff_match_patch } from 'diff-match-patch';
import { useMemo } from 'react';

export type DiffMode = 'base' | 'diff';
export type DiffGranularity = 'paragraph' | 'word';

interface Props {
  before: string;
  after: string;
  mode: DiffMode;
  diffGranularity: DiffGranularity;
}

// Reusable singleton — construction is cheap but avoids per-render overhead.
const dmp = new diff_match_patch();

export function DiffText({
  before,
  after,
  mode,
  diffGranularity,
}: Props) {
  // Word-level diff is memoized per (before, after) pair — diff-match-patch
  // is O(n*m) in the worst case; we don't want to rerun it on every keystroke.
  const wordDiffs = useMemo(() => {
    if (mode !== 'diff' || diffGranularity !== 'word') return null;
    const diffs = dmp.diff_main(before, after);
    dmp.diff_cleanupSemantic(diffs);
    return diffs;
  }, [before, after, mode, diffGranularity]);

  if (mode === 'base') {
    return (
      <span className="whitespace-pre-wrap text-sm text-gray-800">{before}</span>
    );
  }

  if (diffGranularity === 'paragraph') {
    if (before === after) {
      return (
        <span className="whitespace-pre-wrap text-sm text-gray-800">{after}</span>
      );
    }
    return (
      <span className="whitespace-pre-wrap text-sm">
        {before && (
          <del className="bg-red-50 text-red-900 line-through">{before}</del>
        )}
        {before && after && <span> </span>}
        {after && (
          <ins className="ml-1 bg-green-50 text-green-900 no-underline">
            {after}
          </ins>
        )}
      </span>
    );
  }

  // Word-level diff
  if (!wordDiffs) {
    // Shouldn't happen — useMemo returns non-null whenever mode+granularity match.
    return (
      <span className="whitespace-pre-wrap text-sm text-gray-800">{after}</span>
    );
  }

  return (
    <span className="whitespace-pre-wrap text-sm text-gray-800">
      {wordDiffs.map((d, i) => {
        // d[0]: -1=delete, 0=equal, 1=insert. d[1]: segment text.
        const [op, text] = d;
        if (op === 0) {
          return <span key={i}>{text}</span>;
        }
        if (op === 1) {
          return (
            <ins
              key={i}
              className="bg-green-100 text-green-900 no-underline"
            >
              {text}
            </ins>
          );
        }
        return (
          <del
            key={i}
            className="bg-red-100 text-red-900 line-through"
          >
            {text}
          </del>
        );
      })}
    </span>
  );
}
